import { AlertDescription, AlertTitle, Box, Tooltip } from "@chakra-ui/react";
import moment from "moment";
import { ReactNode, useState } from "react";
import { useIntl } from "react-intl";

import { JobRead } from "@nens/rana/api-v1";
import { RanaAlert } from "@nens/ranalib";
import { useAnimationFrame } from "@nens/react/hooks";
import { capitalizeFirst } from "@nens/shared-utils";

import { JobStatusOrCreating } from "@/types/job-status-or-creating";
import { displayJobTime, jobDuration } from "@/utils/job-duration";
import { jobIsRunning } from "@/utils/job-is-running";
import { jobStatusToChakraStatus } from "@/utils/job-status-to-chakra-status";

import { useEstimatedTime } from "../hooks/use-estimated-time";
import { ProcessRunChildProps } from "../types/process-props";

import { ProcessRunStatusFavicon } from "./process-run-status-favicon";
import { ProcessRunStatusLogs } from "./process-run-status-logs";

type StatusDetailProps = { title: string; children: ReactNode };
const StatusDetail = ({ title, children }: StatusDetailProps) => (
  <Box>
    <AlertTitle whiteSpace="nowrap">{title}:</AlertTitle>
    <AlertDescription>{children || <>&nbsp;</>}</AlertDescription>
  </Box>
);

export interface ProcessRunStatusProps extends ProcessRunChildProps {
  status: JobStatusOrCreating;
}

const ProcessRunEta = ({ nowDate, job }: { nowDate: Date; job: JobRead }) => {
  const intl = useIntl();
  const estimatedTime = useEstimatedTime(job);

  if (!estimatedTime) return null;

  if (!job.startedAt)
    return (
      <Tooltip
        hasArrow
        label={intl.formatMessage({
          id: "tooltip.estimated_run_time_explanation",
          defaultMessage:
            "Based on the average duration of previous runs of this process. May be inaccurate because runs with larger inputs can take significantly longer and vice versa.",
        })}
        placement="top"
      >
        <Box>
          <StatusDetail
            title={intl.formatMessage({
              id: "title.estimated_run_time",
              defaultMessage: "Estimated run time",
            })}
          >
            {displayJobTime(moment.duration(estimatedTime))}
          </StatusDetail>
        </Box>
      </Tooltip>
    );

  const etaDate = new Date(new Date(job.startedAt).getTime() + estimatedTime);

  return (
    <Tooltip
      hasArrow
      label={intl.formatMessage({
        id: "tooltip.eta_explanation",
        defaultMessage:
          "Based on current progress and the average duration of previous runs of this process. May be inaccurate because runs with larger inputs can take significantly longer and vice versa.",
      })}
      placement="top"
    >
      <Box>
        <StatusDetail
          title={intl.formatMessage({
            id: "title.eta",
            defaultMessage: "ETA",
          })}
        >
          {etaDate > nowDate
            ? jobDuration(nowDate, etaDate)
            : intl.formatMessage({
                id: "eta.almost_done",
                defaultMessage: "Almost done...",
              })}
        </StatusDetail>
      </Box>
    </Tooltip>
  );
};

const JobStatusDetails = ({ status, job }: ProcessRunStatusProps) => {
  const intl = useIntl();
  const jobRunning = jobIsRunning(status);
  const [nowDate, setNowDate] = useState(new Date());

  useAnimationFrame(() => {
    if (jobRunning && job?.createdAt) setNowDate(new Date());
  }, 10);

  if (!job) return null;

  if (jobRunning)
    return (
      <>
        {job.state.type === "running" && (
          <StatusDetail
            title={intl.formatMessage({
              id: "title.elapsed",
              defaultMessage: "Elapsed",
            })}
          >
            {jobDuration(job.startedAt, nowDate)}
          </StatusDetail>
        )}

        <ProcessRunEta nowDate={nowDate} job={job} />

        {job.state.message && (
          <StatusDetail
            title={intl.formatMessage({
              id: "title.message",
              defaultMessage: "Message",
            })}
          >
            {job.state.message}
          </StatusDetail>
        )}
      </>
    );

  if (status === "completed")
    return (
      <StatusDetail
        title={intl.formatMessage({
          id: "title.took",
          defaultMessage: "Took",
        })}
      >
        {jobDuration(job.startedAt, job.finishedAt)}
      </StatusDetail>
    );

  if (status !== "creating")
    return (
      <StatusDetail title={capitalizeFirst(status) + " at"}>
        {jobDuration(job.startedAt, job.updatedAt)}
      </StatusDetail>
    );

  return null;
};

export function ProcessRunStatus(props: ProcessRunStatusProps) {
  const intl = useIntl();
  const { status, job } = props;

  if (!status) return null;

  const chakraStatus = jobStatusToChakraStatus(status);

  return (
    <>
      <ProcessRunStatusFavicon {...props} />

      <ProcessRunStatusLogs {...props} />
      <RanaAlert
        iconSize={16}
        status={chakraStatus}
        progress={(job?.state.progress ?? 0) * 100}
        gap={11}
      >
        <StatusDetail title="Status">{capitalizeFirst(status)}</StatusDetail>

        <JobStatusDetails key={status} {...props} />

        {job && chakraStatus === "error" && (
          <StatusDetail
            title={intl.formatMessage({
              id: "title.message",
              defaultMessage: "Message",
            })}
          >
            {job.state.message}
          </StatusDetail>
        )}
      </RanaAlert>
    </>
  );
}
